/* frontend/src/components/ImportExcelModal.js */
import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { Upload, AlertCircle, FileSpreadsheet, CheckCircle2, X, Download } from 'lucide-react';
import {
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import api from '../api';

export default function ImportExcelModal({ onClose, onSuccess }) {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [preview, setPreview] = useState([]);
  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    // Validar se é um arquivo Excel
    const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
    if (!validTypes.includes(selectedFile.type)) {
      setError(t('vehicleImport.error'));
      return;
    }

    setFile(selectedFile);
    setError(null);
    parseExcel(selectedFile);
  };

  const parseExcel = async (excelFile) => {
    try {
      const data = await readFileAsync(excelFile);
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      // Pegamos o cabeçalho e alguns dados para preview
      if (jsonData.length > 1) {
        const headers = jsonData[0];
        const previewData = jsonData.slice(1, Math.min(4, jsonData.length)); // Mostra até 3 linhas de dados
        setPreview({ headers, data: previewData });
      } else {
        setError('A planilha parece estar vazia ou mal formatada.');
      }
    } catch (err) {
      console.error('Erro ao processar o arquivo:', err);
      setError('Não foi possível processar o arquivo. Verifique se o formato está correto.');
    }
  };

  const readFileAsync = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(e);
      reader.readAsArrayBuffer(file);
    });
  };

  const handleSubmit = async () => {
    if (!file) return;
    
    try {
      setLoading(true);
      setProgress(0);
      setError(null);
      
      const data = await readFileAsync(file);
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      
      // Enviar dados para a API com controle de progresso
      
      // Iniciamos o upload para o backend
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/import/veiculos/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentage = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgress(percentage);
        }
      });
      
      setProgress(100);
      
      // Se houver erros nas linhas processadas, mostrar alerta
      if (response.data.errors && response.data.errors.length > 0) {
        setError(response.data.message);
        setValidationErrors(response.data.errors);
        setProgress(0);
        return; // Não fechar o modal para o usuário ver os erros
      }
      
      // Preparar informações de resultado para enviar ao componente pai
      const importResult = {
        success: true,
        message: response.data.message,
        count: response.data.data?.length || 0,
        hasErrors: response.data.errors && response.data.errors.length > 0
      };
      
      setTimeout(() => {
        onSuccess(importResult);
        onClose();
      }, 2000); // Damos um tempo maior para o usuário ver a mensagem
    } catch (err) {
      console.error('Erro no upload:', err);
      if (err.response?.data?.errors) {
        setError(err.response.data.message);
        setValidationErrors(err.response.data.errors);
      } else {
        setError(err.response?.data?.message || 'Ocorreu um erro durante a importação. Tente novamente.');
        setValidationErrors([]);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('vehicleImport.title')}</DialogTitle>
      </DialogHeader>

      <div className="space-y-4 p-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {validationErrors.length > 0 && (
          <Alert variant="destructive" className="max-h-64 overflow-y-auto">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-semibold">
                  {validationErrors.length} erro(s) encontrado(s) na planilha:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  {validationErrors.slice(0, 10).map((err, index) => (
                    <li key={index} className="text-left">{err}</li>
                  ))}
                  {validationErrors.length > 10 && (
                    <li className="font-semibold">
                      ... e mais {validationErrors.length - 10} erro(s)
                    </li>
                  )}
                </ul>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <div className="mb-4 flex justify-center">
          <Button 
            variant="outline"
            onClick={() => {
              // Usar a mesma lógica de detecção de ambiente que o api.js
              const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
              const apiBaseURL = isLocalhost 
                ? 'http://localhost:5003'
                : 'https://backoffice-seminovos-api.easycarros.com';
              window.open(`${apiBaseURL}/api/import/veiculos/template`, '_blank');
            }}
            className="flex items-center gap-2 w-full max-w-sm border-dashed border-2 py-6"
          >
            <Download className="h-5 w-5" />
            <span className="font-medium">{t('vehicleImport.downloadTemplate')}</span>
          </Button>
        </div>

        {!file ? (
          <div 
            onClick={() => fileInputRef.current.click()}
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:bg-gray-50 transition-colors"
          >
            <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground mb-1">
              {t('vehicleImport.dropFile')}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('vehicleImport.supportedFormats')}
            </p>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              ref={fileInputRef}
              className="hidden"
            />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 border rounded-md bg-muted/50">
              <FileSpreadsheet className="h-6 w-6 text-green-600" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{file.name}</p>
                <p className="text-xs text-muted-foreground">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setFile(null);
                  setPreview([]);
                  fileInputRef.current.value = '';
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {preview && preview.headers && (
              <div className="rounded-md border overflow-hidden">
                <div className="text-sm font-medium p-2 bg-muted">
                  Preview dos dados
                </div>
                <div className="overflow-x-auto max-h-[300px]">
                  <div className="max-w-[600px] overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/60 sticky top-0">
                        <tr>
                          {preview.headers.map((header, idx) => (
                            <th key={idx} className="p-2 text-left border-b whitespace-nowrap">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.data.map((row, rowIdx) => (
                          <tr key={rowIdx}>
                            {row.map((cell, cellIdx) => (
                              <td key={cellIdx} className="p-2 border-b whitespace-nowrap">
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {loading && (
              <div className="space-y-2">
                <div className="h-2 w-full bg-muted overflow-hidden rounded-full">
                  <div 
                    className="h-full bg-primary transition-all duration-300" 
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  {progress < 100 ? `${t('vehicleImport.processing')} ${progress}%` : (
                    <span className="flex items-center justify-center gap-1 text-green-600">
                      <CheckCircle2 className="h-3 w-3" /> {t('vehicleImport.success')}
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>
        )}

        <div className="text-xs text-muted-foreground mt-4">
          <p className="font-medium mb-1">{t('vehicleImport.instructions')}</p>
          <ol className="list-decimal pl-4 space-y-1">
            <li>{t('vehicleImport.instruction1')}</li>
            <li>{t('vehicleImport.instruction2')}</li>
            <li>{t('vehicleImport.instruction3')}</li>
            <li>{t('vehicleImport.instruction4')}</li>
          </ol>
        </div>
      </div>

      <DialogFooter className="sm:justify-between p-4">
        <Button 
          variant="outline" 
          onClick={onClose} 
          disabled={loading}
          className="sm:w-24"
        >
          {t('common.cancel')}
        </Button>
        <div className="flex gap-2">
          <Button 
            variant="secondary"
            onClick={handleSubmit}
            disabled={!file || loading}
            className="flex items-center gap-1"
          >
            <Upload className="h-4 w-4" />
            {loading ? `${progress}%` : t('vehicleImport.process')}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}